/**
 * Metadata extractors for ProcessBar across all three blueprints.
 */

import type { ProcessBarMetadata } from '@/components/verdict/ProcessBar';
import type { ResearchSession } from '@/hooks/useResearchSession';

/**
 * Extract ProcessBar metadata from a General research session.
 */
export function getGeneralProcessMetadata(session: ResearchSession): ProcessBarMetadata {
  const taskCount = session.tasks.length || session.totalTasks || 0;
  const agentCount = session.agents.length || 0;
  const debateCount = session.debatesPerTask.size;

  let durationSeconds: number | undefined;
  if (session.reportData?.durationSeconds) {
    durationSeconds = session.reportData.durationSeconds;
  } else if (session.researchStartTime) {
    durationSeconds = (Date.now() - session.researchStartTime) / 1000;
  }

  return {
    taskCount: taskCount || undefined,
    agentCount: agentCount || undefined,
    debateRounds: debateCount || undefined,
    durationSeconds,
    label: 'Research Process',
  };
}

/**
 * Extract ProcessBar metadata from a Financial streaming state.
 */
export function getFinancialProcessMetadata(state: {
  bullAgents: Array<{ status: string }>;
  bearAgents: Array<{ status: string }>;
  debateRound: number;
  phase: string;
}): ProcessBarMetadata {
  const agentCount = state.bullAgents.length + state.bearAgents.length;

  return {
    agentCount: agentCount || undefined,
    debateRounds: state.debateRound || undefined,
    label: 'Analysis Process',
  };
}

/**
 * Extract ProcessBar metadata from a Strategic streaming state.
 */
export function getStrategicProcessMetadata(state: {
  workstreams: Array<{ agents: unknown[] }>;
  totalAgents: number;
  debateRounds: number;
  startedAt?: string;
  completedAt?: string;
}): ProcessBarMetadata {
  let durationSeconds: number | undefined;
  if (state.startedAt && state.completedAt) {
    durationSeconds = (new Date(state.completedAt).getTime() - new Date(state.startedAt).getTime()) / 1000;
  } else if (state.startedAt) {
    durationSeconds = (Date.now() - new Date(state.startedAt).getTime()) / 1000;
  }

  return {
    phaseCount: state.workstreams.length || undefined,
    agentCount: state.totalAgents || undefined,
    debateRounds: state.debateRounds || undefined,
    durationSeconds,
    label: 'Analysis Process',
  };
}
